import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getSegmentBySlug, type SegmentCase } from "@/lib/segments-data";
import { getLocalizedSegment } from "@/lib/segments-i18n";
import { SegmentPage } from "@/components/pages/SegmentPage";

const SITE = "https://kentro.com.br";

export const Route = createFileRoute("/$locale/segmentos/$slug")({
  loader: ({ params }) => {
    const segment = getSegmentBySlug(params.slug);
    if (!segment) throw notFound();
    return { slug: segment.slug };
  },
  head: ({ params }) => {
    const base = getSegmentBySlug(params.slug);
    if (!base) return { meta: [], links: [] };
    const loc: "en" | "es" = params.locale === "es" ? "es" : "en";
    const s = getLocalizedSegment(base, loc);
    const url = `${SITE}/${loc}/segmentos/${params.slug}`;
    const ptUrl = `${SITE}/segmentos/${params.slug}`;

    const titleByLoc = {
      en: `Kentro for ${s.name} — Omnichannel support with AI and Official WhatsApp`,
      es: `Kentro para ${s.name} — Atención omnicanal con IA y WhatsApp Oficial`,
    };
    const title = titleByLoc[loc];
    const desc = `${s.tagline} ${loc === "en" ? "Real results in" : "Resultados reales en"} ${s.name.toLowerCase()}: ${s.metrics.slice(0, 3).map((m) => `${m.value} ${m.label}`).join("; ")}.`.slice(0, 300);

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      description: desc,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      about: s.name,
      articleSection: s.category,
      inLanguage: loc === "es" ? "es" : "en",
      author: { "@type": "Organization", name: "Kentro Sistemas" },
      publisher: {
        "@type": "Organization",
        name: "Kentro Sistemas",
        logo: { "@type": "ImageObject", url: `${SITE}/apple-touch-icon.png` },
      },
    };

    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:locale", content: loc === "es" ? "es_ES" : "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "pt-BR", href: ptUrl },
        { rel: "alternate", hrefLang: "en", href: `${SITE}/en/segmentos/${params.slug}` },
        { rel: "alternate", hrefLang: "es", href: `${SITE}/es/segmentos/${params.slug}` },
        { rel: "alternate", hrefLang: "x-default", href: ptUrl },
      ],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
    };
  },
  component: LocalizedSegmentPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold">Segment not found</h1>
      <Link to="/" className="mt-4 inline-block text-primary-glow">Back to home</Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold">Error loading segment</h1>
    </div>
  ),
});

function LocalizedSegmentPage() {
  const { slug } = Route.useLoaderData();
  const segment = getSegmentBySlug(slug) as SegmentCase;
  return <SegmentPage baseSegment={segment} />;
}
